import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useRoute, useNavigation, useIsFocused } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import AITutorChatContainer from "../containers/chat/AITutorChatContainer/AITutorChatContainer";
import LearningGoalsContainer from "../containers/companion/LearningGoalsContainer";
import {
  useGetCompanionChatByAiTutorIdQuery,
  useGetAiTutorVideosQuery,
  useGetAiTutorQuery,
  useGetCourseChatResourcesQuery,
} from "../graphql/generated/graphql";
import { useAppState } from "../contexts/AppStateContext";
import UpgradeModalContainer from "../containers/general/UpgradeModalContainer/UpgradeModalContainer";
import AiTutorCompleteModal from "../containers/customCoursework/AiTutorCompleteModal";
import { sortResourceItemsOfModules } from "../utils/sortResourceItemsOfModules";
import BlankLayout from "../layouts/BlankLayout";
import { colors, radii, spacing, typographyStyles } from "../styles/design-system";
import ExplorationMenu, { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const isObjectiveCompleted = (objective: { status?: string | null; isCompleted?: boolean | null }): boolean => {
  if (typeof objective.isCompleted === "boolean") {
    return objective.isCompleted;
  }

  return objective.status?.trim().toLowerCase() === "completed";
};

const CustomCourseAITutorChatView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course AITutor">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const isFocused = useIsFocused();
  const { resource_id, course_id, integration_id, resource_type, module_id } = route.params;
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [aiTutorCompleteModalVisible, setAiTutorCompleteModalVisible] = useState(false);
  const [isAiResponseStreaming, setIsAiResponseStreaming] = useState(false);
  const [shouldOpenCompletionModalAfterStreaming, setShouldOpenCompletionModalAfterStreaming] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const { state: appState } = useAppState();

  const {
    data: aiTutorData,
    loading: aiTutorLoading,
    refetch: refetchAiTutor,
  } = useGetAiTutorQuery({
    variables: { id: resource_id },
    skip: !resource_id,
  });

  const { data: companionChatByAiTutorData, loading: companionChatByAiTutorLoading } =
    useGetCompanionChatByAiTutorIdQuery({
      variables: {
        aiTutorId: resource_id,
      },
      skip: !resource_id,
      fetchPolicy: "network-only",
    });

  const { data: aiTutorVideosData, refetch: refetchAiTutorVideos } = useGetAiTutorVideosQuery({
    variables: { id: resource_id },
    skip: !resource_id,
    fetchPolicy: "network-only",
  });

  const { data: courseData } = useGetCourseChatResourcesQuery({
    variables: { id: course_id },
    skip: !course_id,
  });

  const formattedModules = useMemo(() => {
    return courseData?.getCourseChatResources.data[0].modules.map((module) => {
      const aiTutors = module.aiTutors.map((aiTutor) => ({
        ...aiTutor,
        resourceType: "aiTutor",
      }));

      const axioResources = module.axioResources.map((resource) => ({
        ...resource,
        resourceType: "axioResource",
      }));

      const assignments = module.assignments.map((assignment) => ({
        ...assignment,
        resourceType: "assignment",
      }));

      const assessments = module.assessment ? [{ ...module.assessment, resourceType: "assessment" }] : [];

      const projects = module.projects.map((project) => ({
        ...project,
        resourceType: "project",
      }));

      const allResources = [...aiTutors, ...axioResources, ...assignments, ...assessments, ...projects];

      return {
        moduleId: module.id,
        resources: sortResourceItemsOfModules(allResources, module.resourceOrder),
      };
    });
  }, [courseData]);

  const currentModuleIndex = formattedModules?.findIndex((m) => m.moduleId === module_id);

  const currentModule = formattedModules?.[currentModuleIndex ?? -1];

  const currentResourceIndex = currentModule?.resources.findIndex((r) => r.id === resource_id);

  const nextActivity = useMemo(() => {
    if (
      !formattedModules ||
      currentModuleIndex === null ||
      currentModuleIndex < 0 ||
      !currentModule ||
      currentResourceIndex === null ||
      currentResourceIndex < 0
    ) {
      // next available activity cannot be found
      return null;
    }

    if (currentResourceIndex < currentModule.resources.length - 1) {
      // next activity is in the current module
      return currentModule.resources[currentResourceIndex + 1];
    }

    const nextModule = formattedModules[currentModuleIndex + 1];
    if (nextModule && nextModule.resources.length > 0) {
      // this was the last activity in the module, returning first activity of the next module
      return nextModule.resources[0];
    }

    // no next activity exists
    return null;
  }, [formattedModules, currentModuleIndex, currentModule, currentResourceIndex]);

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const moduleId = route.params?.module_id;
      if (moduleId && typeof moduleId === "string") {
        const integrationId =
          typeof route.params?.integration_id === "string" ? route.params.integration_id : course_id;
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: course_id,
          integration_id: integrationId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: course_id,
          integration_id,
        });
      }
    }
  };

  const handleBackPress = () => {
    setAiTutorCompleteModalVisible(false);
    handleExitPress();
  };

  const handleNextActivityPress = () => {
    if (!nextActivity) return;

    setAiTutorCompleteModalVisible(false);

    const targetModuleId = formattedModules?.find((m) => m.resources.some((r) => r.id === nextActivity.id))?.moduleId;

    switch (nextActivity.resourceType) {
      case "aiTutor":
        navigation.navigate("Custom Course AITutor", {
          resource_type: "aiTutor",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "assignment":
        navigation.navigate("Custom Assignment Student", {
          resource_type: "assignment",
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;

      case "project":
        navigation.navigate("Project", {
          id: nextActivity.id,
          courseId: course_id,
          integrationId: integration_id,
        });
        break;

      case "axioResource":
        navigation.navigate("Custom Resource Student", {
          resource_type: nextActivity.resourceType,
          resource_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;
      case "assessment":
        navigation.navigate("Custom Course Assessment", {
          assessment_id: nextActivity.id,
          course_id,
          integration_id,
          module_id: targetModuleId,
        });
        break;

      default:
        console.warn("⚠️ Unknown next activity type", nextActivity);
    }
  };

  const handleCloseUpgradeModals = () => {
    setUpgradeModalVisible(false);
  };

  const explorationItems = useMemo<ExplorationMediaItem[]>(() => {
    const videos = aiTutorVideosData?.getAITutorVideos?.data ?? [];
    return videos.map((video) => ({
      id: video.id,
      title: video.title,
      type: "video",
      metadata: "Suggested",
      thumbnail:
        video.thumbnailUrl || (video.videoId ? `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg` : ""),
      videoId: video.videoId,
      description: video.description ?? undefined,
      mediaUrl: video.url ?? undefined,
    }));
  }, [aiTutorVideosData]);

  const handleObjectiveCompleted = () => {
    if (!resource_id) {
      return;
    }

    void Promise.all([refetchAiTutor({ id: resource_id }), refetchAiTutorVideos({ id: resource_id })]);
  };

  useEffect(() => {
    if (appState.meta.isAtDayLimit || appState.meta.isAtLifetimeLimit) {
      setUpgradeModalVisible(true);
    }
  }, [appState.meta.isAtDayLimit, appState.meta.isAtLifetimeLimit]);

  const objectiveData = useMemo(() => {
    const objectives = aiTutorData?.getAITutor?.data?.[0]?.objectives ?? [];
    return objectives.reduce((acc, objective) => {
      if (!objective) {
        return acc;
      }

      acc.push({
        ...objective,
        isCompleted: isObjectiveCompleted(objective),
      });
      return acc;
    }, []);
  }, [aiTutorData]);

  useEffect(() => {
    if (!resource_id) {
      return;
    }

    refetchAiTutorVideos();
  }, [objectiveData, refetchAiTutorVideos, resource_id]);

  const handleAllObjectivesCompleted = () => {
    setShouldOpenCompletionModalAfterStreaming(true);
  };

  const handleAiStreamCompleted = () => {
    if (!shouldOpenCompletionModalAfterStreaming) {
      return;
    }

    setAiTutorCompleteModalVisible(true);
    setShouldOpenCompletionModalAfterStreaming(false);
  };

  useEffect(() => {
    if (!shouldOpenCompletionModalAfterStreaming || isAiResponseStreaming) {
      return;
    }

    setAiTutorCompleteModalVisible(true);
    setShouldOpenCompletionModalAfterStreaming(false);
  }, [isAiResponseStreaming, shouldOpenCompletionModalAfterStreaming]);

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomButton
              onPress={handleExitPress}
              size="s"
              text="Back to course"
              styleType="secondary"
              onHover={(hovered) => setIsBackHovered(hovered)}
              style={[styles.backButton, isBackHovered && styles.backButtonHover]}
              textStyle={styles.backButtonText}
              accessibilityLabel="Exit button"
            />
            <CustomText
              text={aiTutorData?.getAITutor?.data?.[0]?.title ?? ""}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              aria-level="1"
              role="heading"
              style={styles.headerTitle}
            />
          </View>
        </View>

        <View style={styles.content}>
          <View style={styles.chatArea}>
            {!aiTutorLoading ? (
              <LearningGoalsContainer objectives={aiTutorData?.getAITutor?.data?.[0]?.objectives ?? []} />
            ) : null}
            <View style={styles.chatInner}>
              <AITutorChatContainer
                chatData={companionChatByAiTutorData?.getCompanionChatByAiTutorId?.data}
                chatLoading={companionChatByAiTutorLoading}
                resourceId={resource_id}
                isScreenFocused={isFocused}
                onObjectiveCompleted={handleObjectiveCompleted}
                onAllObjectivesCompleted={handleAllObjectivesCompleted}
                onStreamingStateChange={setIsAiResponseStreaming}
                onStreamCompleted={handleAiStreamCompleted}
              />
            </View>
          </View>
          {explorationItems.length > 0 ? <ExplorationMenu items={explorationItems} /> : null}
        </View>

        <UpgradeModalContainer visible={upgradeModalVisible} onClose={handleCloseUpgradeModals} />
        <AiTutorCompleteModal
          visible={aiTutorCompleteModalVisible}
          onBackPress={handleBackPress}
          onNextPress={handleNextActivityPress}
          hasNextActivity={Boolean(nextActivity)}
        />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[200],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
    gap: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    minHeight: 36,
    padding: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
  },
  backButtonHover: {
    backgroundColor: colors.grey[200],
  },
  backButtonText: {
    ...typographyStyles.textSm,
    ...typographyStyles.fontRegular,
    color: colors.grey[900],
  },
  headerTitle: {
    color: colors.grey[900],
  },
  content: {
    flex: 1,
    position: "relative",
    backgroundColor: colors.grey[50],
  },
  chatArea: {
    flex: 1,
    position: "relative",
  },
  chatInner: {
    flex: 1,
    width: "100%",
    alignSelf: "stretch",
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export default CustomCourseAITutorChatView;
